import { useEffect, useMemo, useState } from 'react';
import { IssuerCommunicationConfirmation } from '@/modules/issuer-dashboard/components/communications/IssuerCommunicationConfirmation.tsx';
import {
  usePreviewExistingBulkReportChildrenMutation,
  useSendExistingBulkReportChildrenMutation,
} from '@/modules/issuer-dashboard/queries/communications.ts';
import { Alert, BaseModalProps, Button, ModalContentWrapper, Spinner } from 'ui';
import { AlertIcon } from 'ui/icons';
import type { CommunicationAttachmentPreviewResponse } from 'api';

interface IssuerExistingBulkReportCommunicationModalProps extends BaseModalProps {
  parentReportId: number;
}

export const IssuerExistingBulkReportCommunicationModal = ({
  open,
  onClose,
  parentReportId,
}: IssuerExistingBulkReportCommunicationModalProps) => {
  const [preview, setPreview] = useState<CommunicationAttachmentPreviewResponse | null>(null);
  const {
    mutateAsync: previewExistingBulkReportChildren,
    isLoading: isPreviewLoading,
    isError: isPreviewError,
  } = usePreviewExistingBulkReportChildrenMutation();
  const { mutateAsync: sendExistingBulkReportChildren, isLoading: isSending } =
    useSendExistingBulkReportChildrenMutation();

  const payload = useMemo(
    () => ({
      parent_report_id: parentReportId,
    }),
    [parentReportId],
  );

  useEffect(() => {
    if (!open) {
      setPreview(null);
      return;
    }

    let isActive = true;

    void previewExistingBulkReportChildren(payload)
      .then(response => {
        if (isActive) {
          setPreview(response);
        }
      })
      .catch(() => undefined);

    return () => {
      isActive = false;
    };
  }, [open, payload, previewExistingBulkReportChildren]);

  const handleConfirm = async () => {
    await sendExistingBulkReportChildren(payload);
    onClose();
  };

  return (
    <ModalContentWrapper
      open={open}
      onClose={onClose}
      title="Share bulk report"
      classes={{ dialogPanel: 'lg:!max-w-5xl' }}
    >
      {preview ? (
        <IssuerCommunicationConfirmation
          preview={preview}
          isSubmitting={isSending}
          onBack={onClose}
          onConfirm={handleConfirm}
        />
      ) : isPreviewError ? (
        <div className="space-y-4">
          <Alert icon={AlertIcon} variant="error" className="mt-2 mb-0">
            <div className="whitespace-normal">
              Unable to load the bulk report sharing preview. Close this modal and try again.
            </div>
          </Alert>
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
        </div>
      ) : (
        <div className="h-[220px] flex items-center justify-center">
          <Spinner size="lg" className={isPreviewLoading ? undefined : 'invisible'} />
        </div>
      )}
    </ModalContentWrapper>
  );
};
